import type { ColumnDefinition } from './types';

type SortableColumn = Pick<ColumnDefinition<never>, 'dataType'>;

/**
 * Pure row-value comparator extracted from ConfigurableTable processedData.
 * Preserves exact null-last semantics, number branch, Date branch, and
 * localeCompare('pl') fallback. When column.dataType === 'number', parses
 * string-backed numeric values before comparing; falls back to localeCompare
 * if either side is NaN.
 */
export function compareCellValues(
  aValue: unknown,
  bValue: unknown,
  column: SortableColumn,
  sortDirection: 'asc' | 'desc',
): number {
  // Nulls last on asc, nulls first on desc
  if (aValue == null && bValue == null) return 0;
  if (aValue == null) return sortDirection === 'asc' ? 1 : -1;
  if (bValue == null) return sortDirection === 'asc' ? -1 : 1;

  let comparison = 0;

  if (column.dataType === 'number') {
    const aNum = parseFloat(String(aValue));
    const bNum = parseFloat(String(bValue));
    if (!isNaN(aNum) && !isNaN(bNum)) {
      comparison = aNum - bNum;
    } else if (isNaN(aNum) && !isNaN(bNum)) {
      return sortDirection === 'asc' ? 1 : -1;
    } else if (isNaN(bNum) && !isNaN(aNum)) {
      return sortDirection === 'asc' ? -1 : 1;
    } else {
      comparison = String(aValue).localeCompare(String(bValue), 'pl');
    }
  } else if (typeof aValue === 'number' && typeof bValue === 'number') {
    comparison = aValue - bValue;
  } else if (aValue instanceof Date && bValue instanceof Date) {
    comparison = aValue.getTime() - bValue.getTime();
  } else {
    comparison = String(aValue).localeCompare(String(bValue), 'pl');
  }

  return sortDirection === 'asc' ? comparison : -comparison;
}
